import { X, Trash2, Sparkles, Save, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Cover } from "@/components/Cover";
import { usePlayer } from "@/context/PlayerContext";
import { usePlaylists } from "@/context/PlaylistsContext";
import { fmtTime, formatApiError } from "@/lib/api";
import { MAX_SEEDS, useBuildMix } from "@/lib/mix";

const defaultName = () => `Queue · ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

export const QueueDrawer = ({ open, onOpenChange }) => {
  const p = usePlayer();
  const navigate = useNavigate();
  const { create } = usePlaylists();
  const buildMixFrom = useBuildMix();
  const [saving, setSaving] = useState(false);
  const songs = p.queue.filter((t) => !t.live);
  const buildMix = () => { onOpenChange(false); buildMixFrom(songs); };
  const saveQueue = async () => {
    const name = window.prompt("Playlist name", defaultName());
    if (name === null) return;
    setSaving(true);
    try {
      const ids = [...new Set(songs.map((t) => t.id))];
      const pl = await create(name.trim() || defaultName(), ids);
      toast.success(`Saved ${ids.length} song${ids.length === 1 ? "" : "s"} as "${pl.name}"`, { action: { label: "Open", onClick: () => { onOpenChange(false); navigate(`/playlists/${pl.id}`); } } });
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full bg-white/95 p-0 backdrop-blur-xl sm:max-w-md" data-testid="queue-drawer">
        <SheetHeader className="flex flex-row items-center justify-between border-b border-zinc-200 px-6 py-5">
          <SheetTitle className="font-heading text-xl">Queue <span className="font-mono text-xs text-zinc-400">{p.queue.length}</span></SheetTitle>
          {p.queue.length > 0 && (
            <button onClick={p.clear} data-testid="queue-clear-button" className="mr-8 flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900">
              <Trash2 size={13} /> Clear
            </button>
          )}
        </SheetHeader>
        {songs.length > 0 && (
          <div className="flex gap-2 border-b border-zinc-100 px-6 py-3">
            <button onClick={saveQueue} disabled={saving} data-testid="queue-save-playlist-button" title="Keep this listening session as a playlist"
              className="flex h-9 flex-1 items-center justify-center gap-2 rounded-full bg-zinc-900 px-4 text-xs font-medium text-white transition-colors hover:bg-zinc-700 disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}Save as playlist
            </button>
            <button onClick={buildMix} data-testid="queue-build-mix-button" title={`Open the Playlist Builder with these songs as seeds (${Math.min(songs.length, MAX_SEEDS)})`}
              className="flex h-9 flex-1 items-center justify-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 text-xs font-medium text-amber-900 transition-colors hover:border-amber-600">
              <Sparkles size={13} />Build a mix
            </button>
          </div>
        )}
        <div className="h-[calc(100vh-80px)] overflow-y-auto px-3 py-3">
          {p.queue.length === 0 && <p className="px-3 py-10 text-sm text-zinc-500">Your queue is empty.</p>}
          {p.queue.map((t, i) => (
            <div key={`${t.id}-${i}`} data-testid={`queue-item-${i}`}
              className={`group flex items-center gap-3 rounded-md px-3 py-2 transition-colors ${i === p.index ? "bg-amber-50" : "hover:bg-zinc-50"}`}>
              <button onClick={() => p.playAt(i)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <Cover albumId={t.album_id} className="h-10 w-10 shrink-0" iconSize={14} />
                <div className="min-w-0">
                  <p className={`truncate text-sm ${i === p.index ? "font-semibold text-amber-700" : ""}`}>{t.title}</p>
                  <p className="truncate text-xs text-zinc-500">{t.artist}</p>
                </div>
              </button>
              <span className="font-mono text-[11px] text-zinc-400">{fmtTime(t.duration)}</span>
              <button onClick={() => p.removeAt(i)} data-testid={`queue-remove-${i}`} className="text-zinc-300 opacity-0 transition-opacity hover:text-zinc-900 group-hover:opacity-100">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
